import { isValidAmountInput } from '../../utils/amountValidation';

/**
 * The form rules for dividing a room into bookable segments — #1714.
 *
 * ── Why this is a copy, and what keeps it honest ───────────────────────────
 *
 * The authority is `new-backend/admin/src/lib/inventory/divideRoom.ts`
 * (`parseSegments` / `assessDivisibility`). The browser cannot import it, so
 * this is the second copy CLAUDE.md's "One Rule, One Place" allows only under
 * its condition: the copies name each other and are pinned by a test. The pin
 * is `new-backend/admin/src/lib/inventory/feContract.test.ts`, which reads both
 * files and fails if the bounds drift apart.
 *
 * The division of labour is deliberate. This module decides whether the SUBMIT
 * BUTTON is enabled; the backend decides whether the division happens. It does
 * not duplicate `assessDivisibility` at all — whether a room is a single-object
 * room, already divided, or carries future bookings is a question about live
 * database rows, and answering it in the browser would be a second answer that
 * can disagree. That verdict arrives as a 409 and is rendered by
 * `refusalKeyFor` below.
 *
 * What counts as an admissible amount is NOT re-decided here either: it is
 * `isValidAmountInput`, the same rule the deposit modal and the product form
 * use.
 */

/** #1714: "whole room / half A / half B, up to three sub-segments".
 *  Pinned against MIN_SEGMENTS / MAX_SEGMENTS in the backend rules module. */
export const MIN_SEGMENTS = 2;
export const MAX_SEGMENTS = 3;

/** One row of the dialog's form state. Prices are held as the operator typed
 *  them — validated, never coerced, per the edit-form rule. */
export interface SegmentDraft {
    name: string;
    /** As entered; '' means "no price yet", which is a legitimate answer. */
    price: string;
    /**
     * `validity.badInput` from the number field.
     *
     * Load-bearing: Firefox reports junk in an `<input type="number">` as
     * `value === ''` with this set, so without it "12abc" reads as "no price"
     * and the operator gets a segment nobody can book instead of a validation
     * message (the reason `CurrencyAmountInput` surfaces it at all).
     */
    priceBadInput: boolean;
    /**
     * How many guests this part holds, as entered. **Required** — unlike the
     * price, '' is not a legitimate answer.
     *
     * The asymmetry is deliberate and is the PO's rule: a part with no price is
     * offered as "price on request", which is a real state a customer can act
     * on; a part with no capacity is silently UNRESTRICTED, which is an
     * over-booking surface nobody can see. So one may be left blank and the
     * other may not.
     */
    capacity: string;
    /**
     * `validity.badInput` from the capacity field — the same trap `price` has.
     *
     * Firefox reports junk in an `<input type="number">` as `value === ''` with
     * this set. Without it "40abc" reads as an EMPTY capacity, and the operator
     * is told to fill in a field they just filled in — a wrong attribution of
     * the #2255 family. The price field has carried this defence since #1714;
     * the capacity field needs it for the same reason.
     */
    capacityBadInput: boolean;
    /**
     * The description shown to customers, prefilled from the room and editable.
     *
     * Held as a string rather than as "unset": once the dialog has prefilled it,
     * what the operator sees is what gets sent, and an empty field means they
     * cleared it on purpose.
     */
    description: string;
}

export type SegmentProblem =
    | 'count'
    | 'name_missing'
    | 'name_duplicate'
    | 'price_invalid'
    | 'capacity_missing'
    | 'capacity_invalid';

/**
 * Is this draft submittable?
 *
 * Returns the FIRST problem rather than a list: the dialog has at most three
 * rows, so the operator can see which one is wrong without being told twice.
 */
export const validateSegments = (drafts: SegmentDraft[]): SegmentProblem | null => {
    if (drafts.length < MIN_SEGMENTS || drafts.length > MAX_SEGMENTS) return 'count';

    const names = drafts.map((d) => d.name.trim());
    if (names.some((name) => name.length === 0)) return 'name_missing';

    const lowered = names.map((name) => name.toLowerCase());
    if (new Set(lowered).size !== lowered.length) return 'name_duplicate';

    // An empty price is allowed and means "not bookable yet" — the backend
    // creates the segment and reports it in `unbookableSegments`.
    if (drafts.some((d) => d.priceBadInput || !isValidAmountInput(d.price.trim()))) {
        return 'price_invalid';
    }

    // Capacity is required, and absence is reported separately from a bad
    // value: "you skipped a field" and "that is not a party size" have
    // different remedies, and one message for both is the #2255 failure.
    //
    // `badInput` is checked FIRST and deliberately: Firefox hands back an empty
    // value for junk, so asking "is it empty?" first would tell an operator who
    // typed "40abc" that they left the field blank.
    if (drafts.some((d) => d.capacityBadInput)) return 'capacity_invalid';
    if (drafts.some((d) => d.capacity.trim().length === 0)) return 'capacity_missing';
    if (drafts.some((d) => !isWholeGuestCount(d.capacity.trim()))) return 'capacity_invalid';

    return null;
};

/**
 * Whole people, at least one — the browser half of the backend's own test.
 *
 * Zero is rejected rather than accepted, because `guestCapacityOf` maps
 * anything <= 0 back to null and a stored 0 would come back out as
 * "unrestricted" — the exact state this field exists to prevent, reached by
 * obeying the operator.
 *
 * Deliberately NOT `Number(value) > 0`: that admits `1e3`, `0x10` and
 * whitespace-padded junk, and `parseInt` would silently accept "40 Personen"
 * as 40.
 */
const isWholeGuestCount = (value: string): boolean => {
    if (!/^\d+$/.test(value)) return false;
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) && parsed >= 1;
};

/**
 * The request body, from a draft that has already passed `validateSegments`.
 *
 * A segment with no price sends no `price` key at all rather than `null`: the
 * backend treats absent and null identically, and omitting it keeps "the
 * operator did not answer" distinct from "the operator answered zero" on the
 * wire, where 0 is a real, meaningful price (ADR-0018 rule 4).
 */
export interface SegmentPayload {
    name: string;
    price?: number;
    capacity: number;
    description: string;
}

export const toSegmentPayload = (drafts: SegmentDraft[]): SegmentPayload[] =>
    drafts.map((draft) => {
        const trimmed = draft.price.trim();
        const base = {
            name: draft.name.trim(),
            // Required, so it is always sent — there is no "absent means
            // inherit" here, because nothing is inherited.
            capacity: Number(draft.capacity.trim()),
            // Always sent, including '' — by the time the operator submits,
            // the field has been prefilled from the room, so an empty one
            // means they cleared it rather than never answered.
            description: draft.description,
        };
        return trimmed.length === 0 ? base : { ...base, price: Number(trimmed) };
    });

/**
 * The i18n key for a backend refusal.
 *
 * Every `reason` the endpoint can send has its own sentence, because they have
 * different remedies — cancel the bookings, pick a different room, nothing at
 * all ("already divided" is not a fixable state, it means the job is done), or
 * nothing the operator can do ("unsupported tenant" is answered by migrating
 * the tenant, which is our job, not theirs). One message for all of them would
 * put the operator back in devtools, which is the failure #2255 was about.
 *
 * An unrecognised reason returns null so the caller falls back to the
 * backend's own message rather than rendering a raw key (#2223).
 */
export const refusalKeyFor = (reason: string | undefined): string | null => {
    switch (reason) {
        case 'already_divided':
            return 'services.divide_room.refusal_already_divided';
        case 'not_a_single_room':
            return 'services.divide_room.refusal_not_a_single_room';
        case 'future_bookings':
            return 'services.divide_room.refusal_future_bookings';
        case 'part_of_composite':
            return 'services.divide_room.refusal_part_of_composite';
        case 'unsupported_tenant':
            return 'services.divide_room.refusal_unsupported_tenant';
        case 'unsupported_use_case':
            return 'services.divide_room.refusal_unsupported_use_case';
        // #2647: prices were typed, but the whole room has no price rows to
        // price its parts on — `assessSegmentPricing` in
        // new-backend/admin/src/lib/inventory/divideRoom.ts.
        case 'whole_room_unpriced':
            return 'services.divide_room.refusal_whole_room_unpriced';
        // The payload refusals the boundary added with the capacity
        // requirement (ADR-0020). The dialog blocks both before submit, so an
        // operator meets these only from an older client or a direct call —
        // which is exactly when a raw English server string would be the only
        // thing on screen.
        case 'segment_capacity_missing':
            return 'services.divide_room.refusal_capacity_missing';
        case 'segment_capacity_invalid':
            return 'services.divide_room.refusal_capacity_invalid';
        case 'whole_capacity_invalid':
            return 'services.divide_room.whole_capacity_invalid';
        case 'whole_capacity_below_segment':
            return 'services.divide_room.whole_capacity_below_segment';
        default:
            return null;
    }
};

/** The parts' capacities that are already whole numbers ≥ 1. */
const segmentCapacities = (drafts: SegmentDraft[]): number[] =>
    drafts
        .map((draft) => Number(draft.capacity.trim()))
        .filter((n) => Number.isInteger(n) && n >= 1);

/**
 * The suggested capacity of the WHOLE room: the sum of its parts — the room
 * with its partitions open. Empty until every part has a capacity, so the
 * suggestion never shows a half-typed sum.
 */
export const suggestWholeCapacity = (drafts: SegmentDraft[]): string => {
    const capacities = segmentCapacities(drafts);
    if (capacities.length !== drafts.length) return '';
    return String(capacities.reduce((sum, n) => sum + n, 0));
};

/**
 * Why the whole-room capacity cannot be saved, or null. Blank is allowed
 * (the stored value stays); otherwise whole people and never below the
 * largest part — the server enforces the same rule
 * (`parseWholeRoomCapacity`, new-backend/admin/src/lib/inventory/divideRoom.ts).
 */
export const wholeCapacityProblem = (
    raw: string,
    drafts: SegmentDraft[],
): 'whole_capacity_invalid' | 'whole_capacity_below_segment' | null => {
    const text = raw.trim();
    if (text === '') return null;
    const value = Number(text);
    if (!Number.isInteger(value) || value < 1) return 'whole_capacity_invalid';
    const capacities = segmentCapacities(drafts);
    if (capacities.length > 0 && value < Math.max(...capacities)) {
        return 'whole_capacity_below_segment';
    }
    return null;
};
